import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { HardDrive, Server, Database } from 'lucide-react';
export function MemoryUtilSlide() {
  return <SlideLayout slideNumber={13} totalSlides={20} title="Memory Architecture" data-id="element-398">
      <div className="w-full flex flex-col" data-id="element-399">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-3" data-id="element-400">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-1" data-id="element-401">
            Superior <span className="text-brand-primary" data-id="element-402">Memory</span>{' '}
            Utilization
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-brand-muted max-w-3xl mx-auto" data-id="element-403">
            GPUs carry general-purpose memory overhead. FPGAs dedicate custom
            memory hierarchies to model-specific access patterns.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3" data-id="element-404">
          {[{
          icon: Server,
          title: 'Bare-Metal Operation',
          body: <>
                  No OS kernel, no virtual memory, no background processes
                  competing for bandwidth. FPGA logic directly schedules memory
                  accesses{' '}
                  <span className="text-white" data-id="element-405">
                    cycle-by-cycle with deterministic timing
                  </span>
                  .
                </>,
          delay: 0.2
        }, {
          icon: HardDrive,
          title: 'Weight-Stationary Dataflow',
          body: <>
                  Active layer weights stream from HBM into on-chip{' '}
                  <span className="text-white" data-id="element-406">BRAM/URAM buffers</span>. Custom
                  dataflow keeps these buffers fed with zero idle cycles —
                  activations stream through, achieving massive weight reuse per
                  layer and minimizing redundant HBM fetches.
                </>,
          delay: 0.35
        }, {
          icon: Database,
          title: 'Co-Designed Hierarchy',
          body: <>
                  GPU memory hierarchies are fixed for generality (L1/L2 caches,
                  register files). FPGA memory can be architected for{' '}
                  <span className="text-white" data-id="element-407">
                    your model's exact access pattern
                  </span>{' '}
                  — sized buffer depths, custom banking, optimized for
                  sequential decode or batched prefill as needed.
                </>,
          delay: 0.5
        }].map(card => <motion.div key={card.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: card.delay
        }} className="bg-brand-surface border border-brand-primary/20 rounded-xl p-4 flex flex-col" data-id="element-408">
              <div className="p-2 bg-brand-primary/10 w-fit rounded-lg text-brand-primary mb-2" data-id="element-409">
                <card.icon size={22} data-id="element-410" />
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-1.5" data-id="element-411">
                {card.title}
              </h3>
              <p className="text-brand-muted text-xs md:text-sm leading-relaxed" data-id="element-412">
                {card.body}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.7
      }} className="bg-brand-surface border border-brand-primary/30 rounded-xl p-4 text-center relative overflow-hidden" data-id="element-413">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-primary/50 via-brand-primary to-brand-primary/50" data-id="element-414" />
          <h3 className="text-sm text-brand-muted font-mono uppercase tracking-wider mb-1" data-id="element-415">
            Measured Benchmark
          </h3>
          <span className="text-3xl md:text-4xl font-mono font-bold text-brand-primary" data-id="element-416">
            3,136×
          </span>
          <p className="text-base md:text-lg text-white font-medium mt-0.5 mb-0.5" data-id="element-417">
            Weight Reuse Factor
          </p>
          <p className="text-xs md:text-sm text-brand-muted" data-id="element-418">
            Reducing off-chip traffic from ~440 MB to ~0.87 MB per decode step
            in measured benchmarks by keeping the working set resident on-chip.
          </p>
          <p className="text-xs text-brand-muted mt-2 opacity-60" data-id="element-419">
            Sources: [46][47]
          </p>
        </motion.div>
      </div>
    </SlideLayout>;
}